if (!customElements.get("parallax-section")) {
  customElements.define(
    "parallax-section",
    class Parallax extends HTMLElement {
      constructor() {
        super();
        this.images = [];
        this._scrollHandler = null;
        this._rafId = null;
      }

      _getScrollY() {
        return Math.max(
          window.scrollY || 0,
          window.pageYOffset || 0,
          document.documentElement.scrollTop || 0,
          document.body.scrollTop || 0
        );
      }

      _updateGalleryScroll() {
        const gallery = this.gallery;
        const section = this._section;
        if (!gallery || !section) return;
        const scrollY = this._getScrollY();
        const rect = section.getBoundingClientRect();
        const sectionTop = rect.top + scrollY;
        const rawDelta = Math.max(0, scrollY - sectionTop);
        const maxDelta = Math.max(0, gallery.scrollHeight - window.innerHeight);
        const delta = Math.min(rawDelta, maxDelta);
        this._rawDelta = rawDelta;
        this._maxDelta = maxDelta;
        gallery.style.transform = `translate3d(0, ${-delta}px, 0)`;
      }

      _updateContentPinState() {
        const rect = this.getBoundingClientRect();
        const reachedGalleryEnd =
          typeof this._rawDelta === "number" &&
          typeof this._maxDelta === "number" &&
          this._rawDelta >= this._maxDelta;
        const isEnded = reachedGalleryEnd || rect.bottom <= window.innerHeight;
        const isPinned = rect.top <= 0 && !isEnded && rect.bottom > window.innerHeight;

        this.classList.toggle("parallax-section--pinned", isPinned);
        this.classList.toggle("parallax-section--ended", isEnded);
      }

      connectedCallback() {
        this.images = this.querySelectorAll(".wt-parallax__img");
        this.gallery = this.querySelector(".wt-parallax__gallery");
        this._section = this.closest("section.wt-parallax") || this;
        this.init();
      }

      decorateImages() {
        const listItems = this.querySelectorAll(".wt-parallax__gallery__item");
        listItems.forEach((item, index) => {
          const image = item.querySelector(".wt-parallax__img");
          if (image) {
            image.classList.add(
              `wt-parallax__img--${index % 2 === 0 ? "odd" : "even"}`,
            );
          }
        });
      }

      updateRotation() {
        const { images } = this;
        if (!images.length) return;
        const maxRotation = parseInt(this.dataset.rotation, 10) || 5;
        const windowHeight = window.innerHeight;

        images.forEach((image) => {
          const imageRect = image.getBoundingClientRect();

          if (imageRect.top < windowHeight) {
            let visiblePart = windowHeight - imageRect.top;
            let visiblePercent = (visiblePart / windowHeight) * 100;
            let rotationDegree = Math.min(
              (visiblePercent / 100) * maxRotation,
              maxRotation,
            );

            if (image.classList.contains("wt-parallax__img--odd")) {
              rotationDegree *= -1;
            }

            image.style.transform = `translate3d(0px, 0px, 0px) rotate(${rotationDegree}deg)`;
          } else {
            // Below viewport: don't set transform so images stay in document flow and scroll up naturally
            image.style.removeProperty("transform");
          }
        });
      }

      _onScroll() {
        if (this._rafId != null) return;
        this._rafId = window.requestAnimationFrame(() => {
          this._updateGalleryScroll();
          this._updateContentPinState();
          this.updateRotation();
          this._rafId = null;
        });
      }

      rotateOnScroll() {
        this._scrollHandler = this._onScroll.bind(this);
        window.addEventListener("scroll", this._scrollHandler, { passive: true });
        this._updateGalleryScroll();
        this._updateContentPinState();
        this.updateRotation();
      }

      disconnectedCallback() {
        if (this._scrollHandler) {
          window.removeEventListener("scroll", this._scrollHandler);
        }
        if (this._rafId != null) {
          window.cancelAnimationFrame(this._rafId);
        }
      }

      init() {
        this.decorateImages();
        this.rotateOnScroll();
      }
    },
  );
}
